/**
 * LEDLayersPanel - Configure LED appearance per layer
 * Spec §5.13: LED per layer (USER-EDITABLE)
 */

import { useState } from 'react';
import { useConfigStore } from '@/store/configStore';
import { rgbToHex, hexToRgb } from '@/lib/colorUtils';
import type { LEDLayerConfig, LEDLayerMode } from '@/types/config';

const LED_MODES: { value: LEDLayerMode; label: string; description: string }[] = [
  { value: 'off', label: 'Off', description: 'LEDs are turned off' },
  { value: 'dim', label: 'Dim', description: 'LEDs at reduced brightness' },
  { value: 'full', label: 'Full', description: 'LEDs at full brightness' },
  { value: 'pulse', label: 'Pulse', description: 'LEDs pulse/breathe' },
];

interface LEDLayerEditorProps {
  layerId: number;
  layerName: string;
  config: LEDLayerConfig | undefined;
  onSave: (config: LEDLayerConfig) => void;
  onRemove: () => void;
}

function LEDLayerEditor({ layerId, layerName, config, onSave, onRemove }: LEDLayerEditorProps) {
  const [baseColor, setBaseColor] = useState<[number, number, number]>(
    config?.base_color ?? [0, 128, 255]
  );
  const [brightness, setBrightness] = useState(config?.brightness ?? 0.5);
  const [mode, setMode] = useState<LEDLayerMode>(config?.mode ?? 'dim');

  const hasConfig = config !== undefined;

  const handleSave = () => {
    onSave({
      layer: layerId,
      base_color: baseColor,
      brightness,
      mode,
    });
  };

  const colorHex = rgbToHex(baseColor);

  return (
    <div
      style={{
        padding: '12px',
        marginBottom: '12px',
        backgroundColor: hasConfig ? '#e9ecef' : '#f9f9f9',
        border: `1px solid ${hasConfig ? '#e9ecef' : '#ddd'}`,
        borderRadius: '4px',
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '12px',
        }}
      >
        <span style={{ fontSize: '14px', fontWeight: 'bold' }}>
          Layer {layerId}: {layerName}
          {hasConfig && <span style={{ color: '#28a745', marginLeft: '8px' }}>(configured)</span>}
        </span>
        {hasConfig && (
          <button
            onClick={onRemove}
            style={{
              padding: '2px 8px',
              fontSize: '11px',
              color: '#dc3545',
              backgroundColor: 'transparent',
              border: '1px solid #dc3545',
              borderRadius: '4px',
              cursor: 'pointer',
            }}
          >
            Remove
          </button>
        )}
      </div>

      {/* Color picker */}
      <div style={{ marginBottom: '12px' }}>
        <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px' }}>
          Base Color:
        </label>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <input
            type="color"
            value={colorHex}
            onChange={(e) => setBaseColor(hexToRgb(e.target.value))}
            style={{
              width: '48px',
              height: '32px',
              border: '1px solid #e9ecef',
              borderRadius: '4px',
              cursor: 'pointer',
            }}
          />
          <span style={{ fontSize: '12px', color: '#6c757d', fontFamily: 'monospace' }}>
            RGB({baseColor[0]}, {baseColor[1]}, {baseColor[2]})
          </span>
          {/* Preview swatch showing effective color */}
          <div
            style={{
              width: '32px',
              height: '32px',
              borderRadius: '4px',
              border: '1px solid #e9ecef',
              backgroundColor: `rgb(${Math.round(baseColor[0] * brightness)}, ${Math.round(baseColor[1] * brightness)}, ${Math.round(baseColor[2] * brightness)})`,
            }}
            title="Preview at current brightness"
          />
        </div>
      </div>

      {/* Brightness slider */}
      <div style={{ marginBottom: '12px' }}>
        <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px' }}>
          Brightness: {Math.round(brightness * 100)}%
        </label>
        <input
          type="range"
          min={0}
          max={100}
          value={Math.round(brightness * 100)}
          onChange={(e) => setBrightness(parseInt(e.target.value) / 100)}
          style={{ width: '100%', cursor: 'pointer' }}
        />
      </div>

      {/* Mode selector */}
      <div style={{ marginBottom: '12px' }}>
        <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px' }}>
          Mode:
        </label>
        <select
          value={mode}
          onChange={(e) => setMode(e.target.value as LEDLayerMode)}
          style={{
            width: '100%',
            padding: '6px',
            fontSize: '13px',
            border: '1px solid #e9ecef',
            borderRadius: '4px',
          }}
        >
          {LED_MODES.map((m) => (
            <option key={m.value} value={m.value}>
              {m.label} - {m.description}
            </option>
          ))}
        </select>
      </div>

      {/* Save button */}
      <button
        onClick={handleSave}
        style={{
          width: '100%',
          padding: '8px',
          fontSize: '13px',
          color: '#fff',
          backgroundColor: hasConfig ? '#28a745' : '#007bff',
          border: 'none',
          borderRadius: '4px',
          cursor: 'pointer',
        }}
      >
        {hasConfig ? 'Update Layer LED Config' : 'Set Layer LED Config'}
      </button>
    </div>
  );
}

export function LEDLayersPanel() {
  const layers = useConfigStore((state) => state.layers);
  const ledLayers = useConfigStore((state) => state.ledLayers);
  const ledEnabled = useConfigStore((state) => state.ledSettings.enabled);
  const addLEDLayer = useConfigStore((state) => state.addLEDLayer);
  const removeLEDLayer = useConfigStore((state) => state.removeLEDLayer);

  const handleSave = (config: LEDLayerConfig) => {
    addLEDLayer(config);
  };

  const handleRemove = (layerId: number) => {
    removeLEDLayer(layerId);
  };

  return (
    <>
      {/* Disabled warning */}
      {!ledEnabled && (
        <div
          style={{
            padding: '8px',
            marginBottom: '12px',
            backgroundColor: '#e9ecef',
            border: '1px solid #ffc107',
            borderRadius: '4px',
            fontSize: '12px',
            color: '#856404',
          }}
        >
          LEDs are disabled. Enable them in LED Settings to see layer colors.
        </div>
      )}

      {/* Description */}
      <div
        style={{
          marginBottom: '16px',
          fontSize: '13px',
          color: '#6c757d',
        }}
      >
        Configure LED appearance for each layer. Layer LED settings have priority 2 in the LED hierarchy
        (after per-key overrides, before scale coloring).
      </div>

      {/* Layer editors */}
      {layers.map((layer) => {
        const config = ledLayers.find((l) => l.layer === layer.id);
        return (
          <LEDLayerEditor
            key={layer.id}
            layerId={layer.id}
            layerName={layer.name}
            config={config}
            onSave={handleSave}
            onRemove={() => handleRemove(layer.id)}
          />
        );
      })}

      {/* Info box */}
      <div
        style={{
          padding: '8px',
          backgroundColor: '#e7f3ff',
          borderRadius: '4px',
          fontSize: '11px',
          color: '#0056b3',
        }}
      >
        <strong>LED Priority (§5.16):</strong>
        <ol style={{ margin: '4px 0 0 0', paddingLeft: '20px' }}>
          <li>Per-key LED overrides</li>
          <li><strong>Layer LED config (this panel)</strong></li>
          <li>Press state animation</li>
          <li>Scale coloring</li>
          <li>Default idle color</li>
        </ol>
      </div>
    </>
  );
}
